import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../ui/Button";

/**
 * The confirmation of a dangerous action (WEB-DBG-CMP-03, WEB-VD-DEL-01): it names the device and what will happen,
 * and, with `code`, needs that code typed (the last four hex digits of the device id) before it can go ahead.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  code,
  codeLabel,
  confirmLabel,
  ready = true,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  code?: string;
  /** What to type, when the code is not the device id's last four digits. */
  codeLabel?: string;
  confirmLabel: string;
  /** False while the fields the dialog asks for (in `children`) are not filled in. */
  ready?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const ids = { title: useId(), code: useId() };
  const [typed, setTyped] = useState("");

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (open && !d.open) {
      setTyped("");
      d.showModal();
    } else if (!open && d.open) d.close();
  }, [open]);

  const ok = ready && (!code || typed.trim().toLowerCase() === code.toLowerCase());
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!ok) return;
    onConfirm();
    onClose();
  }

  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      aria-labelledby={ids.title}
      className="m-auto w-full max-w-md rounded-lg p-0 shadow-xl backdrop:bg-black/40"
    >
      <form onSubmit={submit} className="p-6">
        <h2 id={ids.title} className="text-lg font-semibold text-red-900">
          {title}
        </h2>
        <div className="mt-3 space-y-2 text-sm text-gray-900">{children}</div>
        {code && (
          <>
            <label htmlFor={ids.code} className="mt-4 block text-sm font-medium">
              {codeLabel ?? t("console.confirm.type", { code })}
            </label>
            <input
              id={ids.code}
              autoComplete="off"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="mt-1 w-32 rounded-md border border-gray-300 px-2 py-1.5 font-mono text-sm"
            />
          </>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={!ok} className="bg-red-800 text-white hover:bg-red-900">
            {confirmLabel}
          </Button>
        </div>
      </form>
    </dialog>
  );
}
